import TurnoverCard from "../TurnoverCard";
import CAByHourCard from "../CAByHourCard";
import CommercialCard from "../CommercialCard";
import SectorRadiusCard from "../SectorRadiusCard";
import styled from "styled-components";
import Box from "../Box";
import {
  CommercialUnitDataHour,
  CommercialUnitDataWithBrand,
} from "../../types/commercial";
import { computeRHRate } from "../../utils/turnover";
import ComparativeCard from "../ComparativeCard";
import { Range } from "../../utils/range";
import { useTranslation } from "react-i18next";
import { useKpiVisibility } from "../../kpis/useKpiVisibility";
import TurnoverTryptichCard from "../TurnoverTriptychCard";
import { useCallback } from "react";
import { useLocation } from "react-router";
import { ContentsProps } from "./types";
import { useCommercialContentPageMetrics } from "./useCommercialContentPageMetrics";

const TwoColumnsCardsContainer = styled(Box)<{ gap: number; $hasTriptych?: boolean }>`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  align-items: stretch;
  gap: ${(props) => `${props.gap}px`};
  width: 100%;

  /* Desktop and Tablet view: stack cards vertically */
  @media only screen and (min-width: 1024px) {
    display: flex !important;
    flex-direction: column !important;
    justify-content: center !important;
  }

  /* Mobile Landscape view */
  @media only screen and (max-width: 932px) and (orientation: landscape) {
    display: grid !important;
    grid-template-columns: ${({ $hasTriptych }) => ($hasTriptych ? "repeat(2, 1fr)" : "repeat(4, 1fr)")} !important;
    height: auto !important;

    & > * {
      height: 100% !important;
      min-height: 120px !important;
      max-height: none !important;
      flex-shrink: 0 !important;
    }
  }
`;


const CardsContainer = styled(Box)<{ gap: number }>`
  gap: ${(props) => `${props.gap}px`};

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    flex-shrink: 0 !important;
  }
`;
const OrderContainer = styled(Box)<{ hasByHour: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${(props) => `${props.theme?.space?.[2] ?? 0}px`};
  @media only screen and (min-width: 1024px) {
    display: grid;
    grid-template-columns: 2fr 1fr;
    > :nth-child(3) {
      order: 1;
      grid-column: ${(props) => (props.hasByHour ? "1/3" : "0")};
    }
  }

  @media only screen and (max-width: 932px) and (orientation: landscape) {
    flex-shrink: 0 !important;
    & > * {
      flex-shrink: 0 !important;
    }
  }
`;
const CommercialContentPage = ({
  //global
  storeType,
  range,
  //turnover data
  currentDriveData,
  currentTurnoverData,
  currentTurnoverPromoData,
  currentMemberCardTurnoverData,
  pastTurnoverPromoData,
  pastMemberCardTurnoverData,
  driveObjective,
  isLoadingTurnover,
  hasErrorTurnover,
  openInformationModal,
  pastDriveData,
  pastTurnoverData,
  turnoverObjectiveData,
  //turnover ByHour
  byHourLastYear,
  byHourToday,
  hasTurnoverByHour,
  hasErrorTurnoverByHour,
  isLoadingTurnoverByHour,
  //sector
  sectorListData,
  isLoadingSector,
  hasErrorSector,
  //comparative
  comparativeList,
  hasComparative,
  hasErrorComparative,
  isLoadingComparative,
  // tryptich turnover
  currentTurnoverTriptychData,
  pastTurnoverTriptychData
}: ContentsProps) => {
  const {
    basketRHRate,
    ticketsRHRate,
    promoRHRate,
    memberCardRHRate
  } = useCommercialContentPageMetrics({
    currentTurnoverData,
    pastTurnoverData,
    currentTurnoverPromoData,
    currentMemberCardTurnoverData,
    pastTurnoverPromoData,
    pastMemberCardTurnoverData,
    currentDriveData,
    pastDriveData,
    currentTurnoverTriptychData,
    pastTurnoverTriptychData,
    strictRatioRangeValidation: false,
  });

  const triptychTurnoverRates: () => (CommercialUnitDataWithBrand & { evol: number | null })[] = useCallback(() => (currentTurnoverTriptychData ?? []).map(triptych => {
    const pastTripTych = pastTurnoverTriptychData?.find(p => p.brand === triptych.brand);

    return { ...triptych, evol: computeRHRate(
      triptych?.turnover as number,
      pastTripTych?.turnover as number
    ) } as CommercialUnitDataWithBrand & { evol: number | null };
  }), [currentTurnoverTriptychData, pastTurnoverTriptychData]);

  const formatNumber = (value:number | undefined) => {
    if(value === undefined || value === null)
      return "";
   const isMillion = Math.abs(value) >= 1000000;
   const isThousand = Math.abs(value) >= 1000 && !isMillion;
   const ratio = isMillion ? 1000000 : isThousand ? 1000 : 1;
   const currency = isMillion ?  "M€" : isThousand ? "K€" : "€"
   return !!value ?  `${new Intl.NumberFormat("fr-FR", {
                        maximumFractionDigits: 1,
                      }).format(Math.abs(value/ratio))} ${currency}` : "";
  }

 const ratioToPercentage = (ratio: number  | undefined): number => {
  if (ratio === undefined || ratio === null || Number.isNaN(ratio)) {
    return NaN;
  }
  return parseFloat((ratio * 100).toFixed(1));
}
  const {t} = useTranslation();

  const {isKpiExists} = useKpiVisibility();
  
  const hasTriptych = isKpiExists("Commercial", "TurnoverTriptych") && currentTurnoverTriptychData?.some(item => item.turnover !== null && item.turnover !== undefined);

  const location = useLocation();
  const pathname = location.pathname.replace(/\/$/, "");
  const isLiveOrDefault = range === Range.Direct || pathname === '/commercial' || pathname === '/commercial/live' || pathname === '';

  return (
      <>
        <OrderContainer hasByHour={hasTurnoverByHour}>
          { isKpiExists("Commercial", "Turnover") && (
            <TurnoverCard
              currentTurnoverData={currentTurnoverData}
              pastTurnoverData={pastTurnoverData}
              currentDriveData={currentDriveData}
              pastDriveData={pastDriveData}
              turnoverObjectiveData={turnoverObjectiveData}
              driveObjective={driveObjective}
              isLoading={isLoadingTurnover}
              isUnavailable={hasErrorTurnover || currentTurnoverData?.turnover == null}
              openInformationModal={() => null}
            />
          )}
          

          <TwoColumnsCardsContainer
            width={"100%"}
            gap={8}
            $hasTriptych={hasTriptych}
          >
            { isKpiExists("Commercial", "AverageBasket") && (
              <CommercialCard
                title={t('commercial.panier_moyen')}
                subTitle={t('commercial.hors_ecommerce')}
                turnover={currentTurnoverData?.averageBasket}
                rhRate={basketRHRate as number}
                isLoading={isLoadingTurnover}
                currency={"€"}
                isUnavailable={hasErrorTurnover || currentTurnoverData?.averageBasket === null}
                importedDateTime={currentTurnoverData?.importedDateTime}
                openInformationModal={() => null}
                alignCenterMobileLandscape
              />
            )}
            { isKpiExists("Commercial", "TicketsCount") && (
              <CommercialCard
                title={t('commercial.debits')}
                subTitle={t('commercial.hors_ecommerce')}
                turnover={currentTurnoverData?.ticketsCount}
                rhRate={ticketsRHRate as number}
                isLoading={isLoadingTurnover}
                isUnavailable={hasErrorTurnover || currentTurnoverData?.ticketsCount === null}
                currency={"k"}
                importedDateTime={currentTurnoverData?.importedDateTime}
                openInformationModal={() => null}
                alignCenterMobileLandscape
              />
          )}
          { !isLiveOrDefault && !currentMemberCardTurnoverData?.disabled && isKpiExists("Commercial", "MemberCardTurnover") && (
              <CommercialCard
                title={t('commercial.poids_encarté')}
                subTitle={t('commercial.hors_ecommerce')}
                turnover={ratioToPercentage(currentMemberCardTurnoverData?.turnoverRate)}
                turnoverTotal={formatNumber(currentMemberCardTurnoverData?.turnover)}
                rhRate={memberCardRHRate as number}
                rhRateCurrency={' pt'}
                currency={"%"}
                isLoading={isLoadingTurnover}
                isUnavailable={hasErrorTurnover || currentMemberCardTurnoverData?.turnover === null}
                importedDateTime={currentMemberCardTurnoverData?.importedDateTime}
                openInformationModal={() => null}
                alignCenterMobileLandscape
              />
            )}
            { !isLiveOrDefault && !currentTurnoverPromoData?.disabled && isKpiExists("Commercial", "TurnoverPromo") && (
              <CommercialCard
                title={t('commercial.poids_promo')}
                subTitle={t('commercial.hors_ecommerce')}
                turnover={ratioToPercentage(currentTurnoverPromoData?.turnoverRate)}
                turnoverTotal={formatNumber(currentTurnoverPromoData?.turnover)}
                rhRate={promoRHRate as number}
                rhRateCurrency={' pt'}
                currency={"%"}
                isLoading={isLoadingTurnover}
                isUnavailable={hasErrorTurnover || currentTurnoverPromoData?.turnover === null}
                importedDateTime={currentTurnoverPromoData?.importedDateTime}
                openInformationModal={() => null}
                alignCenterMobileLandscape
              />
            )}
          </TwoColumnsCardsContainer>

          {hasTurnoverByHour && isKpiExists("Commercial", "TurnoverByHour") && (
            <CAByHourCard
              byHourToday={byHourToday as CommercialUnitDataHour[]}
              byHourLastYear={byHourLastYear as CommercialUnitDataHour[]}
              isLoading={isLoadingTurnoverByHour as boolean}
              isUnavailable={hasErrorTurnoverByHour as boolean || byHourToday?.[0]?.turnover === null}
              openInformationModal={() => null}
              storeType={storeType}
              sectorListData={sectorListData}
              importedDateTime={byHourToday?.[0]?.importedDateTime}
            />
          )}

        </OrderContainer>
        <CardsContainer
          display={"flex"}
          flexDirection={["column", "row"]}
          width={"100%"}
          gap={16}
        >
          { isKpiExists("Commercial", "TurnoverBySector") && (
            <SectorRadiusCard
              range={range}
              isUnavailable={hasErrorSector || sectorListData?.[0]?.item?.[0]?.sector === null}
              isLoading={isLoadingSector}
              listValue={sectorListData}
              storeType={storeType}
              openInformationModal={() => null}
            />
          )}
          {hasComparative && isKpiExists("Commercial", "Comparative") &&(
            <ComparativeCard
              isUnavailable={hasErrorComparative as boolean || comparativeList?.[0]?.sector === null} 
              isLoading={isLoadingComparative as boolean}
              listValue={comparativeList!}
              storeType={storeType}
              openInformationModal={() => null}
            />
          )}
        </CardsContainer>
          { isKpiExists("Commercial", "TurnoverTriptych") && currentTurnoverTriptychData!== null && (
            <TurnoverTryptichCard
                title={t('commercial.chiffre_affaires_par_triptych')}
                isLoading={isLoadingTurnover}
                isUnavailable={!hasTriptych}
                triptychTurnoverRates={triptychTurnoverRates()}
                openInformationModal={() => null}
            />
          )}
      </>
  );
};

export default CommercialContentPage;
